import React, { useState, useEffect } from 'react';
import { MdCheckCircle, MdClose, MdWarning, MdInfo, MdArrowUpward } from 'react-icons/md';
import { userRequest } from '../utils/requestMethods';
import { useStateContext } from '../contexts/ContextProvider';
import { toast } from 'react-toastify';
import PromotionConfirmationModal from './PromotionConfirmationModal';

const StudentPromotionModal = ({ 
  isOpen, 
  onClose, 
  students = [], 
  classInfo = {}, 
  schoolCode,
  onPromotionSuccess 
}) => {
  const { school } = useStateContext();
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [nextClass, setNextClass] = useState(null);
  const [promotionPreview, setPromotionPreview] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [studentsToPromote, setStudentsToPromote] = useState([]);
  const [checkResult, setCheckResult] = useState(false);
  const [allFailedDetails, setAllFailedDetails] = useState([]);
  const [showDetailedFailed, setShowDetailedFailed] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedStudents([]);
      setError('');
      setSuccess('');
      setPromotionPreview([]);
      setCheckResult(false);
      setAllFailedDetails([]);
      setShowDetailedFailed(false);
    }
  }, [isOpen]);

  // Find next class in hierarchy
  useEffect(() => {
    // For last classes, create a "Graduated" next class object
    if (classInfo?.is_last_class) {
      const graduatedClass = {
        _id: 'graduated',
        class_name: 'Graduated',
        level_of_education: 'graduated'
      };
      setNextClass(graduatedClass);
      return;
    }

    if (classInfo?.next_class) {
      // Check if nextClassData is already provided by parent component
      if (classInfo.nextClassData) {
        console.log('Next class data from parent:', classInfo.nextClassData);
        setNextClass(classInfo.nextClassData);
        return;
      }
      
      // Try to find next class from school.classes first
      let nextClassData = null;
      if (school?.classes) {
        nextClassData = school.classes.find(cls => cls._id === classInfo.next_class);
      }
      
      // If not found in school.classes, create a minimal next class object
      if (!nextClassData) {
        nextClassData = {
          _id: classInfo.next_class,
          class_name: `Class ${classInfo.next_class.slice(-4)}`, // Fallback name
          level_of_education: classInfo.level_of_education // Assume same level
        };
      }
      
      console.log('Next class data:', nextClassData);
      setNextClass(nextClassData);
    } else {
      setNextClass(null);
    }
  }, [classInfo, school?.classes]);

  const handleStudentSelection = (studentId, isSelected) => {
    if (isSelected) {
      setSelectedStudents(prev => [...prev, studentId]);
    } else {
      setSelectedStudents(prev => prev.filter(id => id !== studentId));
    }
  };

  const handleSelectAll = () => {
    const eligibleStudents = students.filter(student => 
      student.is_active && student.academic_status === 'active'
    );
    
    if (selectedStudents.length === eligibleStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(eligibleStudents.map(student => student._id));
    }
  };

  const handleBulkPromotion = () => {
    if (selectedStudents.length === 0) {
      setError('Please select at least one student to promote');
      return;
    }

    if (!nextClass) {
      setError('No next class found for promotion');
      return;
    }

    // Get student objects for confirmation
    const studentsToPromoteData = students.filter(student => 
      selectedStudents.includes(student._id)
    );
    
    setStudentsToPromote(studentsToPromoteData);
    setShowConfirmation(true);
  };

  const confirmBulkPromotion = async () => {
    setLoading(true);
    setError('');
    setShowConfirmation(false);
    
    try {
      const payload = {
        class_id: classInfo._id, // Current class ID (where students are being promoted FROM)
        school_code: schoolCode,
        student_ids: selectedStudents,
        check_result: checkResult
      };
      
      const response = await userRequest.patch('/children/promote-students', payload);
      const data = response.data;

      const destinationText = classInfo?.is_last_class ? 'to graduated' : `to ${nextClass.class_name}`;
      
      // Handle structured response having success and failure arrays
      const successCount = data.success?.length || 0;
      const failCount = data.failed?.length || 0;
      
      let successMessage = '';
      const maxDisplayItems = 5; // Show max 5 items per category
      
      // Store full failure details for potential detailed view
      if (failCount > maxDisplayItems) {
        setAllFailedDetails(data.failed || []);
      } else {
        setAllFailedDetails([]);
      }
      
      if (successCount > 0) {
        successMessage += `✅ Successfully promoted ${successCount} student(s) ${destinationText}`;
        if (data.success && successCount <= maxDisplayItems) {
          // Show all names if 5 or fewer
          successMessage += ':\n' + data.success.map(s => `• ${s.child_name || s.child_id}`).join('\n');
        } else if (data.success && successCount > maxDisplayItems) {
          // Show first few + count if more than 5
          const displayedSuccess = data.success.slice(0, maxDisplayItems);
          successMessage += ':\n' + displayedSuccess.map(s => `• ${s.child_name || s.child_id}`).join('\n');
          successMessage += `\n  ... and ${successCount - maxDisplayItems} more`;
        }
      }
      
      if (failCount > 0) {
        if (successMessage) successMessage += '\n\n';
        
        if (failCount <= maxDisplayItems) {
          // Show all failures if 5 or fewer
          successMessage += `❌ Failed to promote ${failCount} student(s):\n`;
          successMessage += data.failed.map(f => `• ${f.child_name || f.child_id}: ${f.message}`).join('\n');
        } else {
          // Show summary + first few + details if more than 5
          successMessage += `❌ Failed to promote ${failCount} student(s):\n`;
          
          // Group failures by error message
          const errorGroups = {};
          data.failed.forEach(f => {
            if (!errorGroups[f.message]) {
              errorGroups[f.message] = [];
            }
            errorGroups[f.message].push(f.child_name || f.child_id);
          });
          
          // Show most common errors first
          const sortedGroups = Object.entries(errorGroups)
            .sort((a, b) => b[1].length - a[1].length);
          
          let remainingFailures = failCount;
          sortedGroups.forEach(([errorMsg, studentNames], index) => {
            if (remainingFailures <= 0) return;
            
            const maxShow = Math.min(studentNames.length, 3); // Show max 3 names per error type
            const displayedNames = studentNames.slice(0, maxShow);
            
            successMessage += `\n  ${errorMsg} (${studentNames.length} students):`;
            successMessage += '\n    • ' + displayedNames.join('\n    • ');
            
            if (studentNames.length > maxShow) {
              successMessage += `\n    ... and ${studentNames.length - maxShow} more`;
            }
            
            remainingFailures -= studentNames.length;
            if (remainingFailures <= 0) {
              successMessage += '\n\n📋 Click "View All Failures" below for complete details';
            }
          });
        }
      }
      
      if (successCount === 0 && failCount === 0) {
        successMessage = 'No promotions processed.';
      }
      
      setSuccess(successMessage);
      
      if (successCount > 0) {
        toast.success(`Successfully promoted ${successCount} student(s) ${destinationText}`);
      }
      if (failCount > 0) {
        toast.error(`Failed to promote ${failCount} student(s). Check details above.`);
      }
      
      // Call success callback after delay if any students were promoted successfully
      if (successCount > 0) {
        setTimeout(() => {
          onPromotionSuccess();
          onClose();
        }, 3000); // Give user time to read the detailed results
      }

    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to promote students';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleIndividualPromotion = (student) => {
    if (!nextClass) {
      setError('No next class found for promotion');
      return;
    }

    // Show confirmation for individual promotion too
    setStudentsToPromote([student]);
    setShowConfirmation(true);
  };

  const confirmIndividualPromotion = async () => {
    if (studentsToPromote.length === 0) return;
    
    const student = studentsToPromote[0];
    setLoading(true);
    setError('');
    setShowConfirmation(false);
    
    try {
      const payload = {
        class_id: classInfo._id, // Current class ID (where student is being promoted FROM)
        school_code: schoolCode,
        student_id: student._id,
        check_result: checkResult
      };
      
      const response = await userRequest.patch('/children/promote-student', payload);
      const data = response.data;

      const destinationText = classInfo?.is_last_class ? 'to graduated' : `to ${nextClass.class_name}`;
      
      // Handle structured response with success and failure arrays
      const successCount = data.success?.length || 0;
      const failCount = data.failed?.length || 0;
      
      let successMessage = '';
      
      if (successCount > 0) {
        const successStudent = data.success.find(s => s.child_id === student._id);
        const studentName = successStudent?.child_name || `${student.firstname} ${student.lastname}`;
        successMessage = `✅ Successfully promoted ${studentName} ${destinationText}`;
        
        toast.success(`Successfully promoted ${studentName} ${destinationText}`);
        
        // Call success callback and close modal
        setTimeout(() => {
          onPromotionSuccess();
          onClose();
        }, 2000);
      } else if (failCount > 0) {
        const failedStudent = data.failed.find(f => f.child_id === student._id);
        if (failedStudent) {
          const studentName = failedStudent.child_name || `${student.firstname} ${student.lastname}`;
          successMessage = `❌ Failed to promote ${studentName} ${destinationText}: ${failedStudent.message}`;
          
          toast.error(`Failed to promote ${studentName}: ${failedStudent.message}`);
        } else {
          successMessage = 'Failed to promote student';
          toast.error('Failed to promote student');
        }
      } else {
        successMessage = 'Promotion request processed but no clear result returned';
        toast.warning('Promotion result unclear. Please refresh and check.');
      }
      
      setSuccess(successMessage);

    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to promote student';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const eligibleStudents = students.filter(student => 
    student.is_active && student.academic_status === 'active'
  );

  const allSelected = eligibleStudents.length > 0 && selectedStudents.length === eligibleStudents.length;
  const someSelected = selectedStudents.length > 0 && selectedStudents.length < eligibleStudents.length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-full">
              <MdArrowUpward className="text-blue-600 text-xl" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Promote Students
              </h2>
              <p className="text-sm text-gray-500">
                {classInfo?.class_name} → {nextClass?.class_name || 'No next class'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <MdClose size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Promotion Info */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <MdInfo className="text-blue-600 mt-0.5" size={20} />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Promotion Information</p>
                <ul className="space-y-1 text-blue-700">
                  {classInfo?.is_last_class ? (
                    <>
                      <li>• Students will be promoted from <strong>{classInfo?.class_name}</strong> to <strong>Graduated</strong></li>
                      <li>• This is the final class in the school hierarchy</li>
                      <li>• This will empty the class for new intakes</li>
                      <li>• Student records and academic history will be preserved</li>
                      <li>• Only active students with 'active' academic status can be promoted</li>
                    </>
                  ) : (
                    <>
                      <li>• Students will be promoted from <strong>{classInfo?.class_name}</strong> to <strong>{nextClass?.class_name || 'next class'}</strong></li>
                      <li>• Only active students with 'active' academic status can be promoted</li>
                      <li>• Student records and academic history will be preserved</li>
                      {!nextClass && (
                        <li className="text-red-600">• <strong>Warning:</strong> No next class is configured for this class</li>
                      )}
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>

          {/* Check Result Toggle */}
          <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-900 mb-1">Result Verification</h3>
                <p className="text-sm text-gray-600">
                  {checkResult 
                    ? "Students will only be promoted if they have complete results for the current term/session"
                    : "Students will be promoted regardless of result completion status"
                  }
                </p>
              </div>
              <div className="ml-4">
                <button
                  type="button"
                  onClick={() => setCheckResult(!checkResult)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    checkResult ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      checkResult ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <MdWarning className="text-red-600 mt-0.5" size={20} />
                <div className="text-red-800 whitespace-pre-line">
                  {error}
                </div>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <MdCheckCircle className="text-green-600 mt-0.5" size={20} />
                <div className="flex-1">
                  <div className="text-green-800 whitespace-pre-line">
                    {success}
                  </div>
                  {allFailedDetails.length > 0 && (
                    <div className="mt-3">
                      <button
                        onClick={() => setShowDetailedFailed(!showDetailedFailed)}
                        className="inline-flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-800 font-medium bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
                      >
                        {showDetailedFailed ? 'Hide' : 'View'} All {allFailedDetails.length} Failed Students
                        <svg 
                          className={`ml-1 w-4 h-4 transition-transform ${showDetailedFailed ? 'rotate-180' : ''}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Detailed Failed Students View */}
          {showDetailedFailed && allFailedDetails.length > 0 && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg max-h-60 overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-red-800">All Failed Promotions ({allFailedDetails.length})</h4>
                <button
                  onClick={() => setShowDetailedFailed(false)}
                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  ✕ Close
                </button>
              </div>
              <div className="space-y-2">
                {allFailedDetails.map((failure, index) => (
                  <div key={failure.child_id || index} className="p-2 bg-white rounded border border-red-200">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {failure.child_name || failure.child_id}
                        </p>
                        <p className="text-xs text-red-600 mt-1">
                          {failure.message}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bulk Selection */}
          {eligibleStudents.length > 0 && (
            <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="select-all"
                    checked={allSelected}
                    ref={input => {
                      if (input) input.indeterminate = someSelected;
                    }}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="select-all" className="text-sm font-medium text-gray-700">
                    Select all eligible students ({eligibleStudents.length})
                  </label>
                </div>
                {selectedStudents.length > 0 && (
                  <button
                    onClick={handleBulkPromotion}
                    disabled={loading || !nextClass}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    ) : (
                      <MdArrowUpward className="mr-2" size={16} />
                    )}
                    Promote Selected ({selectedStudents.length})
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Students List */}
          <div className="space-y-3">
            {eligibleStudents.length === 0 ? (
              <div className="text-center py-8">
                <MdWarning className="mx-auto text-gray-400 text-4xl mb-2" />
                <p className="text-gray-500">No eligible students found for promotion</p>
                <p className="text-sm text-gray-400 mt-1">
                  Students must be active with 'active' academic status
                </p>
              </div>
            ) : (
              eligibleStudents.map((student) => (
                <div key={student._id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                  <div className="flex items-center space-x-4">
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(student._id)}
                      onChange={(e) => handleStudentSelection(student._id, e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-medium text-sm">
                            {student.firstname?.[0]}{student.lastname?.[0]}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {student.firstname} {student.lastname}
                          </p>
                          <p className="text-sm text-gray-500">
                            ID: {student.registration_id} • {student.gender}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleIndividualPromotion(student)}
                    disabled={loading || !nextClass}
                    className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600 mr-1"></div>
                    ) : (
                      <MdArrowUpward className="mr-1" size={14} />
                    )}
                    Promote
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Inactive Students Info */}
          {students.length > eligibleStudents.length && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <MdInfo className="inline mr-1" size={16} />
                {students.length - eligibleStudents.length} student(s) are inactive and cannot be promoted
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      <PromotionConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={studentsToPromote.length === 1 ? confirmIndividualPromotion : confirmBulkPromotion}
        selectedStudents={studentsToPromote}
        currentClass={classInfo}
        nextClass={nextClass}
        loading={loading}
      />
    </div>
  );
};

export default StudentPromotionModal;
